import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Star, Phone, Globe, Clock, ExternalLink } from 'lucide-react';
import { VenueFollowButton } from '@/components/venue/VenueFollowButton';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';

interface VenueCardProps {
  venue: {
    id: string;
    venue_name: string;
    address: string;
    city: string;
    state_province: string;
    phone_number?: string;
    website?: string;
    operating_hours?: string;
    amenities?: string[];
    sports_available?: string[];
    rating?: number;
    price_range?: string;
    description?: string;
    hourly_rates?: number;
    profile_photo_url?: string;
  };
  ratingCount?: number;
  onContact?: () => void;
  onViewDetails?: () => void;
}

export const VenueCard = ({ venue, ratingCount, onContact, onViewDetails }: VenueCardProps) => {
  const { user: unifiedUser } = useUnifiedUser();
  const calculatePriceRange = (hourlyRate?: number): string => {
    if (!hourlyRate) return '$';
    if (hourlyRate <= 50) return '$';
    if (hourlyRate <= 100) return '$$';
    if (hourlyRate <= 200) return '$$$';
    return '$$$$';
  };

  const priceDisplay = venue.price_range || calculatePriceRange(venue.hourly_rates);

  return (
    <Card className="bg-gray-900 border-gray-700 overflow-hidden hover:bg-gray-800 transition-all duration-300 hover:shadow-lg hover:shadow-sports-green/20">
      <div className="relative">
        {/* Venue background image with 70% overlay */}
        <div className="h-56 relative">
          {venue.profile_photo_url ? (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${venue.profile_photo_url})` }}
            >
              <div className="absolute inset-0 bg-black/70"></div>
            </div>
          ) : (
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(59, 130, 246, 0.9) 50%, rgba(139, 69, 19, 0.9) 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
          
          {/* Price range badge */}
          {priceDisplay && (
            <Badge className="absolute top-4 left-4 bg-sports-green text-black font-bold px-3 py-1 text-sm">
              {priceDisplay}
            </Badge>
          )}
          
          {/* Rating */}
          {venue.rating && (
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-full border border-white/20">
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              <span className="text-white font-semibold">{venue.rating.toFixed(1)}</span>
              {ratingCount && (
                <span className="text-white/80 text-sm">({ratingCount})</span>
              )}
            </div>
          )}
          
          {/* Venue name */}
          <div className="absolute bottom-16 left-4 right-20">
            <h3 className="text-3xl font-bold text-white drop-shadow-lg">
              {venue.venue_name}
            </h3>
          </div>
          
          {/* Profile photo circle */}
          <div className="absolute bottom-4 right-4">
            <div className="w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden shadow-lg">
              {venue.profile_photo_url ? (
                <img 
                  src={venue.profile_photo_url} 
                  alt={venue.venue_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-sports-green to-blue-600 flex items-center justify-center">
                  <MapPin className="text-white h-8 w-8" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <CardContent className="p-6 bg-gray-900">
        {/* Location */}
        <div className="flex items-center gap-2 text-gray-300 mb-4">
          <MapPin className="h-5 w-5 text-sports-green" />
          <span className="font-medium">{venue.address}, {venue.city}, {venue.state_province}</span>
        </div>

        {/* Description */}
        {venue.description && (
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
            {venue.description}
          </p>
        )}

        {/* Operating hours */}
        {venue.operating_hours && (
          <div className="flex items-center gap-2 text-gray-400 mb-4">
            <Clock className="h-4 w-4" />
            <div className="text-sm">
              {venue.operating_hours.split(',').map((time, index) => (
                <span key={index} className="block">
                  {time.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Amenities */}
        {venue.amenities && venue.amenities.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {venue.amenities.slice(0, 3).map((amenity, index) => (
                <Badge key={index} variant="outline" className="text-xs border-gray-600 text-gray-300">
                  {amenity}
                </Badge>
              ))}
              {venue.amenities.length > 3 && (
                <Badge variant="outline" className="text-xs border-gray-600 text-gray-300">
                  +{venue.amenities.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-2 items-center">
          {venue.phone_number && (
            <Button 
              variant="outline"
              size="sm"
              onClick={onContact}
              className="flex-1 border-gray-600 text-gray-300 hover:text-white"
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact
            </Button>
          )}
          
          <Button 
            className="flex-1 bg-sports-green text-black hover:bg-green-400 font-bold transition-all duration-200"
            onClick={onViewDetails}
          >
            View Details
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
          
          {unifiedUser && (
            <VenueFollowButton
              venueId={venue.id}
              userId={unifiedUser.id}
              userType={unifiedUser.userType}
              size="sm"
              variant="ghost"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};